import React, { useState, useEffect } from 'react';
import { ProjectStatus, GameDescriptionForm as FormData, PlanResponse } from './types/index';
import { apiService } from './services/api';
import GameDescriptionForm from './components/GameDescriptionForm';
import ProgressDisplay from './components/ProgressDisplay';
import AssetGallery from './components/AssetGallery';
import DownloadManager from './components/DownloadManager';
import './index.css';

function App() {
  const [projectStatus, setProjectStatus] = useState<ProjectStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventSource, setEventSource] = useState<EventSource | { close: () => void } | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [showDownloadManager, setShowDownloadManager] = useState(false);
  // 真实联调模式：移除演示模式

  // 清理SSE连接
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  // 创建项目规划
  const handleFormSubmit = async (formData: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('🚀 开始创建素材规划...');

      const planResponse: PlanResponse = await apiService.createPlan(formData);

      console.log('✅ 真实API响应成功，项目ID:', planResponse.projectId);

      // 开始SSE连接监听项目状态
      startSSEConnection(planResponse.projectId);

    } catch (error) {
      console.error('创建项目失败:', error);

      // 更详细的错误提示（不再提及演示模式）
      if (error instanceof Error && error.name === 'AbortError') {
        setError('请求超时，服务器响应时间过长。请稍后再试或检查服务状态。');
      } else if (error instanceof Error && error.message.includes('Failed to fetch')) {
        setError('网络连接失败，请检查网络或CORS设置。');
      } else {
        setError(error instanceof Error ? error.message : '创建项目失败，请重试');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 启动SSE连接
  const startSSEConnection = (projectId: string) => {
    // 关闭现有连接
    if (eventSource) {
      eventSource.close();
    }

    const newEventSource = apiService.createSSEConnection(
      projectId,
      (data: ProjectStatus) => {
        setProjectStatus(data);
        if (data.status === 'completed') {
          setIsConnected(false);
        } else {
          setIsConnected(true);
        }
      },
      (error: Error) => {
        console.error('SSE连接错误:', error);
        setIsConnected(false);
        setError('实时连接断开，请刷新页面重试');
      }
    );

    setEventSource(newEventSource as EventSource);
  };

  // 单个素材下载
  const handleAssetDownload = async (assetId: string, assetName: string, imageUrl: string) => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const fileExtension = imageUrl.split('.').pop() || 'png';
      link.download = `${assetName}.${fileExtension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载失败:', error);
      setError('下载失败，请重试');
    }
  };

  // 批量下载
  const handleDownloadAll = () => {
    if (!projectStatus || projectStatus.assets.length === 0) return;

    const completedAssets = projectStatus.assets.filter(
      asset => asset.status === 'completed' && asset.imageUrl
    );

    if (completedAssets.length === 0) {
      setError('没有可下载的素材');
      return;
    }

    const allAssetIds = new Set(completedAssets.map(asset => asset.assetId));
    setSelectedAssets(allAssetIds);
    setShowDownloadManager(true);
  };

  // 选择素材下载
  const handleDownloadSelected = () => {
    if (selectedAssets.size === 0) {
      setError('请先选择要下载的素材');
      return;
    }
    setShowDownloadManager(true);
  };

  // 重置项目
  const handleReset = async () => {
    if (!projectStatus) return;

    try {
      await apiService.resetProject(projectStatus.projectId);

      // 关闭SSE连接
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }

      // 重置状态
      setProjectStatus(null);
      setIsConnected(false);
      setSelectedAssets(new Set());
      setError(null);

    } catch (error) {
      console.error('重置项目失败:', error);
      setError('重置项目失败，请重试');
    }
  };

  // 回到首页
  const handleBackToHome = () => {
    // 关闭SSE连接
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }

    // 重置状态，返回首页
    setProjectStatus(null);
    setIsConnected(false);
    setSelectedAssets(new Set());
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                🎮 游戏素材生成器
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {projectStatus && (
                <button
                  onClick={handleReset}
                  className="px-4 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  🔄 重新开始
                </button>
              )}
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className="text-sm text-gray-600">
                  {isConnected ? '已连接' : '未连接'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 演示模式提示 */}
        {/* 演示模式提示已移除 */}

        {/* 错误提示 */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400">❌</span>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <span className="sr-only">关闭</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 游戏描述表单 */}
        {!projectStatus && (
          <GameDescriptionForm onSubmit={handleFormSubmit} loading={isLoading} />
        )}

        {/* 进度显示 */}
        {projectStatus && (
          <ProgressDisplay
            projectStatus={projectStatus}
            isConnected={isConnected}
            onBackToHome={handleBackToHome}
          />
        )}

        {/* 素材画廊 */}
        {projectStatus && projectStatus.assets.length > 0 && (
          <>
            <AssetGallery
              assets={projectStatus.assets}
              onDownload={handleAssetDownload}
              onDownloadAll={handleDownloadAll}
              selectedAssets={selectedAssets}
              onAssetSelect={(assetId) => {
                const newSelected = new Set(selectedAssets);
                if (newSelected.has(assetId)) {
                  newSelected.delete(assetId);
                } else {
                  newSelected.add(assetId);
                }
                setSelectedAssets(newSelected);
              }}
            />

            {/* 批量下载按钮 */}
            {selectedAssets.size > 0 && (
              <div className="mt-6 bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">
                    已选择 {selectedAssets.size} 个素材
                  </span>
                  <button
                    onClick={handleDownloadSelected}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    📥 下载选中项
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* 下载管理器 */}
        <DownloadManager
          isVisible={showDownloadManager}
          onClose={() => setShowDownloadManager(false)}
          assets={projectStatus?.assets || []}
          selectedAssets={Array.from(selectedAssets)}
        />
      </main>

      {/* 页脚 */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500">
            🚀 基于 Cloudflare 构建的游戏素材生成系统
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
